import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  REMEDIATION_STATUS_LABELS,
  SERVICE_SEVERITY_LABELS,
  type RemediationStatus,
} from '@bhops/shared';
import { REMEDIATION_TONE, SEVERITY_TONE } from '@/pages/field/field-tones';
import type { CSSProperties } from 'react';
import type { IconName } from '@/components/Icon';
import { teamDashboardApi } from '@/api/team-dashboard';
// 추가수정 2차(26-07-27) 정정 — 표시 날짜는 KST 정본으로.
import { kstYmd } from '@/lib/format-date';
// 개선 9건 차수2f(26-08-04) — 배정·설치 단계 한국어 표기는 정본 한 벌에서만 온다
// (종전엔 이 파일이 같은 표를 따로 들고 있었다 — 화면마다 같은 값을 다른 단어로 불렀다).
import { formatContractHandoff } from '@/lib/contract-handoff';
import {
  AutoRefreshHint,
  DashboardError,
  DashboardSkeleton,
  HorizontalBars,
  KpiCard,
  SHARED_EMPTY_HINT,
  SHARED_REFETCH_INTERVAL_MS,
  Section,
} from './_shared';
import { FieldIssueSummarySection } from './FieldIssueSummarySection';

// 등급 · 상태 표기와 색은 '리포트 · 조치 이력' 화면과 같은 정본을 쓴다 — 대시보드 막대를 누르면 그 화면이
// 같은 이름 · 같은 색으로 걸러져 열린다.
const REMEDIATION_ORDER: RemediationStatus[] = [
  'REVISIT_NEEDED',
  'IN_PROGRESS',
  'OBSERVING',
  'MONITORING',
  'COMPLETED',
];
// 색은 이 화면의 것이다(같은 단계를 다른 화면은 다른 색으로 칠한다) — 표기와 달리
// 정본으로 모을 값이 아니다. 한국어 문구만 lib 한 벌에서 온다.
const HANDOFF_TEXT_COLOR: Record<string, string> = {
  PENDING_SIGN: 'var(--fg-muted)',
  SIGNED_AWAITING_ASSIGNMENT: 'var(--dday-warning)',
  ASSIGNED_AWAITING_INSTALL: 'var(--fg-brand)',
  INSTALLED: 'var(--robot-operating)',
  CANCELLED: 'var(--fg-muted)',
};

export function FieldDashboardPage() {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['dashboard', 'field'],
    queryFn: () => teamDashboardApi.field(),
    staleTime: 60_000,
    refetchInterval: SHARED_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });

  if (q.isLoading) return <DashboardSkeleton kpiCount={4} />;
  // 새 탭이 로그인을 넘겨받기 전처럼 답이 비어 오는 때도 있다 — 하얀 오류 화면 대신 다시 시도.
  if (q.error || !q.data) return <DashboardError onRetry={q.refetch} />;
  const d = q.data;

  return (
    // 2026-10-27 오너 "필드 대시보드부터 손보자" → 사업 대시보드와 같은 모양(흰 숫자 카드 · 색 아이콘 ·
    // 칸 사이 48px · 아래 두 칸 한 줄).
    <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
      {/* KPI cards — 4 개 (clickable) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 8,
        }}
      >
        <KpiCard
          // S3 Phase 3.1 — 분류 큐가 서비스 오더 내부 탭(?view=triage) 으로
          // 통합됨. redirect 바운스 없이 탭 URL 로 직접 진입.
          to="/service-orders?view=triage"
          title="24h SLA 위험"
          value={d.slaRiskNext24hCount}
          hint={
            d.slaRiskNext24hCount > 0
              ? '24시간 안에 SLA를 넘길 오더가 있습니다. 누르면 분류 큐가 열립니다.'
              : '누르면 분류 큐가 열립니다.'
          }
          warningLevel="red"
          icon={KPI_ICON.sla}
        />
        <KpiCard
          // 2026-10-27 오너 "리포트-조치이력 탭이랑 연동이 돼야" — 그 화면의 고객사 리포트(대기 · 진행중).
          to="/field/customer-reports?open=1"
          title="미해결 리포트"
          value={d.openReportsCount}
          hint="대기 또는 진행중인 고객사 리포트입니다. 누르면 '리포트 · 조치 이력'이 열립니다."
          icon={KPI_ICON.reports}
        />
        <KpiCard
          to="/fleet/tracker"
          title="활성 배치"
          value={d.activeDeploymentsCount}
          hint="누르면 '추적 · 분석' 화면이 열립니다."
          icon={KPI_ICON.deployments}
        />
        <KpiCard
          to="/field/install-calendar"
          title="30일 내 설치 예정"
          value={d.upcomingInstalls.length}
          hint="누르면 설치 일정이 열립니다."
          warningLevel="amber"
          icon={KPI_ICON.installs}
        />
      </div>

      {/* 설치 일정 — 리포트 · 조치 이력 위(2026-10-28 오너 "심각도별 미해결 리포트 위로"). */}
      <div style={pairRow}>
        {d.upcomingInstalls.length > 0 ? (
          <Section style={pairCard} title="30일 내 설치 예정 상세">
            {/* 칸 사이 틈 없이 머리글이 한 줄 띠로 이어지게(사업 대시보드 마감 임박 계약 표와 같다). */}
            {/* 머리글 칸 사이 짧은 세로선(globals.css)은 긋는다 — 초록 선을 뺀 것은 칸마다 선이 있는 로봇 운영 이슈 표였다
                (2026-10-28 오너 "여기 사이에 조그만 세로선 넣기로 했었는데"). */}
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={cell}>계약</th>
                  <th style={cell}>고객사</th>
                  <th style={cell}>매장</th>
                  <th style={cell}>예정일</th>
                  <th style={cell}>핸드오프</th>
                </tr>
              </thead>
              <tbody>
                {/* 줄을 누르면 고객사 상세(FB2 §3A 직링크) — 따로 '상세' 칸은 두지 않는다(2026-10-28 오너). */}
                {d.upcomingInstalls.map((c) => (
                  <tr
                    key={c.id}
                    data-testid="upcoming-install-row"
                    tabIndex={0}
                    title="누르면 고객사 상세가 열립니다."
                    style={{ borderTop: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                    onClick={() => navigate(`/customers/${c.customerId}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navigate(`/customers/${c.customerId}`);
                    }}
                  >
                    <td
                      style={{
                        ...cell,
                        fontFamily: 'var(--font-mono)',
                        fontSize: 11,
                      }}
                    >
                      {c.humanId}
                    </td>
                    <td style={cell}>{c.customer.displayName}</td>
                    <td style={{ ...cell, color: c.store ? 'inherit' : 'var(--fg-muted)' }}>
                      {c.store?.name ?? '–'}
                    </td>
                    {/* 추가수정 2차(26-07-27) 정정 — 인프라 발주 목록·매장 상세와
                      같은 설치 예정일. 결정 ②A 대로 KST 날짜 표기로 통일. */}
                    <td style={cell}>{kstYmd(c.scheduledInstallAt) ?? '–'}</td>
                    <td
                      style={{
                        ...cell,
                        fontSize: 11,
                        color: HANDOFF_TEXT_COLOR[c.handoffStatus] ?? 'var(--fg-default)',
                        fontWeight: 500,
                      }}
                      title={c.handoffStatus}
                    >
                      {formatContractHandoff(c.handoffStatus) ?? c.handoffStatus}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        ) : (
          <Section style={pairCard} title="30일 내 설치 예정">
            <div style={SHARED_EMPTY_HINT}>
              예정된 설치가 없습니다. 전체 일정은{' '}
              <Link to="/field/install-calendar" style={linkSmall}>
                설치 일정
              </Link>{' '}
              화면에서 볼 수 있습니다.
            </div>
          </Section>
        )}
      </div>

      {/* '리포트 · 조치 이력' 두 탭의 지금 상황을 한 줄에 나란히(사업 대시보드의 마감 임박 계약 · 갱신 기록과
          같은 모양). 막대를 누르면 그 탭이 그 등급 · 상태로 걸러져 열린다. 종전엔 서비스 오더 심각도를 셌다. */}
      <div style={sideBySide}>
        <Section style={sideCard} title="심각도별 미해결 리포트">
          <HorizontalBars
            items={d.openReportsBySeverity.map((s) => ({
              key: s.severity,
              label: SERVICE_SEVERITY_LABELS[s.severity],
              count: s.count,
              color: SEVERITY_TONE[s.severity].bar,
              onClick: () => navigate(`/field/customer-reports?open=1&severity=${s.severity}`),
            }))}
          />
        </Section>
        <Section style={sideCard} title="고장 조치 이력">
          <HorizontalBars
            items={REMEDIATION_ORDER.map((st) => ({
              key: st,
              label: REMEDIATION_STATUS_LABELS[st],
              count: d.remediationStatusCounts[st] ?? 0,
              color: REMEDIATION_TONE[st].bar,
              onClick: () => navigate(`/field/fault-remediations?status=${st}`),
            }))}
          />
        </Section>
      </div>

      {/* 2026-10-27 오너 — 노션으로 매 보름 만들던 이슈 정리를 고장 조치 이력으로 바로 본다. */}
      <FieldIssueSummarySection />

      <AutoRefreshHint isFetching={q.isFetching} />
    </div>
  );
}

/** 숫자 카드 이름 앞 색 아이콘 — 사업 대시보드(KPI_ICON)와 같은 모양. */
const KPI_ICON = {
  sla: { name: 'siren', color: '#dc2626' },
  reports: { name: 'inbox', color: '#d97706' },
  deployments: { name: 'bot', color: '#16a34a' },
  installs: { name: 'calendar', color: '#2f6fe0' },
} satisfies Record<string, { name: IconName; color: string }>;

const pairRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
  alignItems: 'flex-start',
};
const pairCard: CSSProperties = { flex: '1 1 420px', minWidth: 0 };
// 리포트 · 조치 이력 두 칸은 폭이 좁아도 늘 한 줄에(2026-10-28 오너 "한 줄에 나열해. 폭 조정해서").
const sideBySide: CSSProperties = { display: 'flex', gap: 16, alignItems: 'stretch' };
const sideCard: CSSProperties = { flex: '1 1 0', minWidth: 0 };
const cell: CSSProperties = { padding: '7px 10px', whiteSpace: 'nowrap' };

const linkSmall: CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-brand)',
  textDecoration: 'none',
};
